import type { MenuInstance } from '@szhsin/react-menu'
import { Tooltip, Text } from '@chakra-ui/react'
import { useContext, useMemo, useRef, useState, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  AiFillFacebook,
  AiFillInstagram,
  AiFillLinkedin,
  AiOutlineCopy,
  AiOutlineFileZip,
} from 'react-icons/ai'
import { BsTags, BsTwitterX } from 'react-icons/bs'
import { CgPlayListAdd, CgPlayListRemove } from 'react-icons/cg'
import { LiaEdit } from 'react-icons/lia'
import { GrImage } from 'react-icons/gr'
import {
  HiOutlineDocumentDownload,
  HiOutlineFolderAdd,
  HiOutlineFolderRemove,
} from 'react-icons/hi'
import { IoExtensionPuzzleOutline, IoImagesOutline, IoMoveSharp } from 'react-icons/io5'
import { TbFileSpreadsheet } from 'react-icons/tb'
import {
  MdCenterFocusWeak,
  MdPublishedWithChanges,
  MdOutlineOpenInBrowser,
  MdOutlinePolicy,
  MdRestore,
  MdOutlineShare,
  MdContentCopy,
  MdOutlineAddLink,
  MdOutlineCropOriginal,
  MdExtension,
} from 'react-icons/md'
import { TiTags } from 'react-icons/ti'
import OrganizeIcon from '../../../../graphics/OrganizeIcon'
import { useHistory } from 'react-router-dom'
import numeral from 'numeral'
import useFetch, { CachePolicies } from 'use-http'
import styled from 'styled-components'
import {
  FiExternalLink,
  FiFileMinus,
  FiLink2,
  FiUserPlus,
} from 'react-icons/fi'
import { ChevronRightIcon, createIcon } from '@chakra-ui/icons'
import { TbPhotoSearch } from 'react-icons/tb'
import { ImEmbed } from 'react-icons/im'
import { RiDeleteBin2Line } from 'react-icons/ri'
import { Base64 } from 'js-base64'
import {
  allowExtensionOperations,
  getFileURL,
  isEditableImage,
  SocialUrlTransforms,
  downloadFileFromUrl,
} from '../../../../utils/mediaLibrary'
import { addLeadingSlash } from '../../../../utils/urlHelpers'
import useGetFileUrlArguments from '../../../hooks/useGetFileUrlArguments'
import useToast from '../../../hooks/useToast'
import type {
  ViewableParentSuccessResponse,
  ContextMenuItem,
  CreateDownloadJobResponse,
  CreateDownloadJobErrorResponse,
  MediaLibrarySearchFileResponse,
} from '../../../interfaces'
import { MLContext, UserContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import {
  checkOperationDisabled,
  checkPermission,
  PermissionsEnum,
} from '../../../../utils'
import { ACLPermissionEnum, MLSortParams } from '../../../constants'
import { FeaturesEnum } from '../../../../utils/features'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import { isRestrictedUser } from '../../../../utils/roles'
import { useDetailLinkBase } from '../../../hooks/useDetailLinkBase'
import { useNavigateToDetail } from '../../../hooks/useNavigateToDetail'
import { useBuildDetailUrl } from '../../../hooks/useBuildDetailUrl'
import Hover from '../../Hover'
import { colors } from '../../../../ui-kit'
import {
  removeTrailingSlash,
  removeLeadingSlash,
} from '../../../../utils/urlHelpers'
import ResponsiveTooltip from '../../ResponsiveTooltip'
import { exportSelectedItemsAsCSV } from '../utils/csvExport'

export interface UseContextMenuItemsParams {
  setCopyMoveOperation(v: 'copy' | 'move'): void
  setTagOperation(v: 'updateTags' | 'addTagsBulk' | 'removeTagsBulk'): void
  onOpenCopyMoveDrawer(): void
  onOpenRenameFileModal(): void
  onOpenRenameFolderModal(): void
  onOpenRenameMediaCollectionModal(): void
  onOpenPublishFilesModal(): void
  onOpenTagsModal(): void
  onOpenAutoTagsModal(): void
  onOpenRemoveAssetFromMultipleMediaCollections(): void
  onOpenRemoveMultipleAssetsFromMediaCollection(): void
  onOpenDeleteMediaModal(): void
  onOpenDeleteFileVersionModal(): void
  onOpenRestoreFileVersionModal(): void
  onOpenRemoveBgModal(): void
  onOpenCustomFocusAreaModal(): void
  onOpenShareAssetModal(): void
  onOpenShareMediaCollectionModal(): void
  onOpenPublicLinksModal(): void
  onOpenAddToMediaCollection(): void
  onOpenDownloadModal(): void
  handleEmbedModal(open: boolean): void
  onOpenApplyPathPolicyModal(): void
  onOpenApplySavedExtensionModal(): void
  onOpenImageEditor(): void
  onOpenPhotoEditor(): void
  onOpenBulkEditCustomMetadataModal(): void
  refMap: { [key: string]: React.RefObject<MenuInstance> }
  isDetailModel?: boolean
  file?: MediaLibrarySearchFileResponse
}

const HStackCustom = styled.div<{ gap?: number }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ gap = 8 }) => gap}px;
`

const VStackCustom = styled.div<{ gap?: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ gap = 4 }) => gap}px;
`

const HStackSpacer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const PaddingBox = styled.div`
  padding: 8px 12px;
  width: 100%;
`

export const useContextMenuItems = (
  p: UseContextMenuItemsParams,
): ContextMenuItem[] => {
  const {
    setCopyMoveOperation,
    setTagOperation,
    onOpenCopyMoveDrawer,
    onOpenRenameFileModal,
    onOpenRenameFolderModal,
    onOpenRenameMediaCollectionModal,
    onOpenPublishFilesModal,
    onOpenTagsModal,
    onOpenAutoTagsModal,
    onOpenRemoveAssetFromMultipleMediaCollections,
    onOpenRemoveMultipleAssetsFromMediaCollection,
    onOpenDeleteMediaModal,
    onOpenDeleteFileVersionModal,
    onOpenRestoreFileVersionModal,
    onOpenRemoveBgModal,
    onOpenCustomFocusAreaModal,
    onOpenShareAssetModal,
    onOpenShareMediaCollectionModal,
    onOpenPublicLinksModal,
    onOpenAddToMediaCollection,
    onOpenDownloadModal,
    handleEmbedModal,
    onOpenApplyPathPolicyModal,
    onOpenApplySavedExtensionModal,
    onOpenImageEditor,
    onOpenPhotoEditor,
    onOpenBulkEditCustomMetadataModal,
    refMap,
    isDetailModel,
    file,
  } = p

  let [
    {
      selectedFiles,
      selectedFolders,
      selectedMediaCollections,
      currentMediaCollection,
      mediaCollectionRoute,
      folderPath,
      viewableParent,
      selectedBreadcrumbFolder,
      selectedBreadcrumbMediaCollection,
      refetchFiles,
    },
    dispatch,
  ] = useContext(MLContext)

  if (!file && isDetailModel) {
    return []
  } else if (file && isDetailModel) {
    selectedFiles = [file]
  }

  const {
    isPublicLink,
    urlPrefix,
    clientInfo,
    dashboardServerURL: publicLinkDashboardServerURL,
  } = useContext(PublicLinksInfoContext)

  const { user } = useContext(UserContext)
  const dashboardServerURL =
    user?.dashboardServerURL ?? publicLinkDashboardServerURL

  const history = useHistory()

  const toast = useToast()

  const { t } = useTranslation()

  const { background } = useDetailLinkBase()

  const onOpenFile = useNavigateToDetail()

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    clientInfo: clientInfo,
    user,
  })

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const handleDownloads = useCallback(
    async (transformConfig?: {
      width: string
      height: string
    }): Promise<void> => {
      try {
        let response: any = {}

        try {
          const isSingleFile: boolean =
            selectedFiles.length === 1 &&
            selectedFolders.length === 0 &&
            selectedMediaCollections.length === 0

          const isNonImageOrNonEditableImage = (
            file: MediaLibrarySearchFileResponse,
          ): boolean => !isEditableImage(file) || file.fileType === 'non-image'

          const isTransformAsked = Boolean(
            transformConfig?.width && transformConfig.height,
          )

          if (!dashboardServerURL) {
            throw new Error(
              'Error in downloading items, please try again after sometime',
            )
          }
          if (isSingleFile) {
            const file = selectedFiles[0]
            const downloadURL = `${removeTrailingSlash(
              dashboardServerURL,
            )}/${removeLeadingSlash(urlPrefix)}api/v2/files/${
              file.fileId
            }/download?fileName=${file.name}&responseType=url`

            let finalDownloadUrl = downloadURL

            if (isEditableImage(file) && isTransformAsked) {
              // If the file is an editable image, add the transformation to the URL
              const urlObj = new URL(downloadURL)
              urlObj.searchParams.set(
                'trString',
                JSON.stringify([
                  {
                    width: transformConfig?.width,
                    height: transformConfig?.height,
                    focus: 'auto',
                  },
                ]),
              )
              finalDownloadUrl = urlObj.toString()
              downloadFileFromUrl(finalDownloadUrl)
            } else if (
              isNonImageOrNonEditableImage(file) ||
              !isTransformAsked
            ) {
              downloadFileFromUrl(downloadURL)
            }

            response = {
              success: true,
              isSingleFile: true,
              downloadUrl: finalDownloadUrl,
            }

            return
          }

          // For multiple assets, we use the download job API
          const fileIds = selectedFiles.map(file => file.fileId)
          const folderIds = selectedFolders.map(folder => folder.folderId)
          const mediaCollectionIDs = selectedMediaCollections.map(
            mediaCollection => mediaCollection.mediaCollectionId,
          )

          const items: string[] = [
            ...fileIds,
            ...folderIds,
            ...mediaCollectionIDs,
          ]
          const endpoint = `${urlPrefix}api/v1/download/`

          const res = (await request.post(endpoint, {
            items,
            transformations: isTransformAsked
              ? [
                  {
                    w: transformConfig?.width,
                    h: transformConfig?.height,
                  },
                ]
              : [],
          })) as CreateDownloadJobResponse | CreateDownloadJobErrorResponse

          if (request.error) {
            response = {
              success: false,
              isSingleFile: false,
              error:
                'Error in downloading items, please try again after sometime',
            }
          }

          response = {
            success: true,
            isSingleFile: false,
            jobId: (res as CreateDownloadJobResponse).jobId,
            jobStatus: {
              jobId: (res as CreateDownloadJobResponse).jobId,
              status: (res as CreateDownloadJobResponse).status,
              name: (res as CreateDownloadJobResponse).name,
            },
          }
        } catch (err) {
          response = {
            success: false,
            isSingleFile: false,
            error:
              'Error in downloading items, please try again after sometime',
          }
        }

        if (response.success) {
          if (response.jobId && response.jobStatus) {
            toast({
              title: t(
                'download_success_title',
                'Download request submitted successfully',
              ),
              description: t(
                'download_success_description',
                'Your download is being processed and will begin shortly. ' +
                  // eslint-disable-next-line max-len
                  'You can also download the requested ZIP file from the Downloads section once it is ready.',
              ),
              status: 'success',
              isClosable: true,
              duration: 5000,
              position: 'top-right',
              variant: 'outline',
            })

            dispatch([
              {
                type: MediaLibraryActions.ADD_OPERATION,
                payload: {
                  jobId: response.jobStatus.jobId,
                  status: response.jobStatus.status,
                  name: response.jobStatus.name,
                },
              },
              {
                type: MediaLibraryActions.CLOSE_CONTEXT_MENU,
              },
            ])
          }
        } else if (response.error) {
          throw new Error(response.error)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        toast({
          description: t(
            'error_in_downloading_items',
            'Error in downloading items, please try again after sometime',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      }
    },
    [
      selectedFiles,
      selectedFolders,
      selectedMediaCollections,
      dashboardServerURL,
      urlPrefix,
      request,
      dispatch,
      toast,
      t,
    ],
  )

  const buildDetailUrl = useBuildDetailUrl({
    mediaCollectionRoute,
    currentMediaCollection,
    file: selectedFiles[0],
  })

  const getUrl = useCallback(() => {
    let url = ''
    if (selectedFiles.length === 1) {
      url = buildDetailUrl
    } else if (selectedFolders.length === 1) {
      url = `/dashboard/media-library/${Base64.encode(
        selectedFolders[0].folderPath,
        true,
      )}`
    } else if (selectedMediaCollections.length === 1) {
      url = `/dashboard/media-collections/${selectedMediaCollections[0].mediaCollectionId}`
    }
    return window.location.origin + addLeadingSlash(url)
  }, [
    selectedFiles,
    selectedFolders,
    selectedMediaCollections,
    buildDetailUrl,
    addLeadingSlash,
  ])

  const contextMenuItems = useMemo((): ContextMenuItem[] => {
    const ASSET_IDS_COUNT_LIMIT = 100

    const isFileVersionSelected =
      selectedFiles.filter(item => item.type === 'file-version').length > 0

    const downloadConfiguration: {
      isOnlyFiles: boolean
      isOnlyImageFiles: boolean
      totalCount: number
      totalSize: number | null
      isTemplateDisabled: boolean
      disabledText: string
      copyText: string | null
    } = {
      isOnlyFiles: false,
      isOnlyImageFiles: true,
      totalCount:
        selectedFiles.length +
        selectedFolders.length +
        selectedMediaCollections.length,
      totalSize: null,
      isTemplateDisabled: false,
      disabledText: 'This option is not supported for non-image assets.',
      copyText: 'as a ZIP archive',
    }

    if (
      selectedFiles.length > 0 &&
      selectedFolders.length === 0 &&
      selectedMediaCollections.length === 0
    ) {
      downloadConfiguration.isOnlyFiles = true
      downloadConfiguration.totalCount = selectedFiles.length
      downloadConfiguration.totalSize = selectedFiles.reduce(
        (acc, item) => acc + item.size,
        0,
      )
      downloadConfiguration.isOnlyImageFiles = selectedFiles.every(
        item => item.fileType === 'image',
      )

      if (!downloadConfiguration.isOnlyImageFiles) {
        downloadConfiguration.isTemplateDisabled = true
      }

      if (downloadConfiguration.totalCount === 1) {
        downloadConfiguration.copyText = null
      }
    }

    return [
      {
        name: 'Open',
        element: () => (
          <Hover w="full">
            {isHover => {
              if (isHover) {
                return (
                  <PaddingBox>
                    <HStackSpacer>
                      <HStackCustom>
                        <MdOutlineOpenInBrowser size={16} />
                        {'Open'}
                      </HStackCustom>
                      {!isPublicLink && (
                        <HStackCustom>
                          <div>
                            <ResponsiveTooltip
                              initialLabel="Copy link"
                              getCopyText={getUrl}
                              onClick={(e, isSuccess) => {
                                dispatch({
                                  type: MediaLibraryActions.CLOSE_CONTEXT_MENU,
                                })
                                if (isSuccess) {
                                  toast({
                                    description: t(
                                      'copy_link_success_message',
                                      'URL copied successfully',
                                    ),
                                    status: 'success',
                                    isClosable: true,
                                    duration: 5000,
                                    position: 'top-right',
                                    variant: 'outline',
                                  })
                                } else {
                                  toast({
                                    description: t(
                                      'copy_link_failure_message',
                                      'Failed to copy URL',
                                    ),
                                    status: 'error',
                                    isClosable: true,
                                    duration: 5000,
                                    position: 'top-right',
                                    variant: 'outline',
                                  })
                                }
                              }}
                            >
                              <FiLink2 size={16} />
                            </ResponsiveTooltip>
                          </div>
                          <div
                            onClick={e => {
                              e.stopPropagation()
                              dispatch({
                                type: MediaLibraryActions.CLOSE_CONTEXT_MENU,
                              })
                              const finalUrl = getUrl()
                              window.open(finalUrl, '_blank')
                            }}
                          >
                            <Tooltip
                              label="Open in new tab"
                              placement="top"
                              shouldWrapChildren
                            >
                              <FiExternalLink size={16} />
                            </Tooltip>
                          </div>
                        </HStackCustom>
                      )}
                    </HStackSpacer>
                  </PaddingBox>
                )
              } else {
                return (
                  <PaddingBox>
                    <HStackCustom>
                      <MdOutlineOpenInBrowser size={16} />
                      {'Open'}
                    </HStackCustom>
                  </PaddingBox>
                )
              }
            }}
          </Hover>
        ),
        icon: MdOutlineOpenInBrowser,
        viewConditions:
          selectedFiles.length +
            selectedFolders.length +
            selectedMediaCollections.length ===
          1,
        trigger: () => {
          if (selectedFiles.length === 1) {
            onOpenFile({
              mediaCollectionRoute,
              currentMediaCollection,
              file: selectedFiles[0],
            })
          } else if (selectedFolders.length === 1) {
            void history.push({
              pathname: `/media-library/${Base64.encode(
                selectedFolders[0].folderPath,
                true,
              )}`,
              state: { background },
            })
          } else if (selectedMediaCollections.length === 1) {
            void history.push({
              pathname: `/media-collections/${selectedMediaCollections[0].mediaCollectionId}`,
              state: { background },
            })
          }

          dispatch([
            {
              type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
            },
            {
              type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
            },
          ])
        },
      },
      {
        name: 'Find similar',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <TbPhotoSearch size={16} />
              {t('find_similar', 'Find similar')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: TbPhotoSearch,
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 0 &&
          allowExtensionOperations(selectedFiles[0]),
        trigger: async () => {
          let finalUrl = ''
          const data = await getFileURL({
            file: selectedFiles[0],
            ...getFileUrlDynamicArguments,
            request,
          })
          if (data.includes('ik-s=') && data.includes('ik-auth-version')) {
            const url = new URL(data)
            url.searchParams.delete('ik-auth-version')
            url.searchParams.delete('ik-s')
            url.searchParams.delete('ik-t')
            finalUrl = url.toString()
          } else {
            finalUrl = data
          }
          dispatch([
            { type: MediaLibraryActions.RESET_SEARCH },
            {
              type: MediaLibraryActions.SET_MIN_SCORE,
              payload: 0.8,
            },
            {
              type: MediaLibraryActions.SET_Q,
              payload: finalUrl,
            },
            {
              type: MediaLibraryActions.SET_SEARCH_MODE,
              payload: 'ai-image',
            },
            {
              type: MediaLibraryActions.SET_SORT_PARAM,
              payload: MLSortParams.RELEVANCE,
            },
            {
              type: MediaLibraryActions.SET_OPEN_SEMANTIC_IMAGE_SEARCH,
              payload: false,
            },
            {
              type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
            },
            {
              type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
            },
          ])
        },
        optionDisabledInfo: () => {
          if (
            !user?.pricingPlan.featuresList.includes(
              FeaturesEnum.SEMANTIC_SEARCH,
            )
          ) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'semantic_search_not_available',
                'Visual search is not available in your current plan. Please upgrade to access this feature.',
              ),
            }
          }
          return {
            isDisabled: false,
          }
        },
      },
      {
        name: 'Organize',
        viewConditions:
          !isPublicLink &&
          !isFileVersionSelected &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }),
        element: () => (
          <PaddingBox>
            <HStackSpacer>
              <HStackCustom>
                <OrganizeIcon />
                {'Organize'}
              </HStackCustom>
              <ChevronRightIcon
                fontWeight={400}
                boxSize={5}
                color={colors.imagekitGray['500']}
              />
            </HStackSpacer>
          </PaddingBox>
        ),
        reference: refMap.organize,
        subMenu: {
          items: [
            {
              name: 'Copy',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <AiOutlineCopy />
                    {'Copy to'}
                  </HStackCustom>
                </PaddingBox>
              ),
              icon: AiOutlineCopy,
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedMediaCollections.length === 0 &&
                !isFileVersionSelected,
              trigger: () => {
                dispatch([
                  {
                    type: MediaLibraryActions.SET_DRAWER_SEARCH_FILTER,
                    payload: '',
                  },
                  {
                    type: MediaLibraryActions.SET_DRAWER_SEARCH_QUERY,
                    payload: '',
                  },
                ])
                setCopyMoveOperation('copy')
                onOpenCopyMoveDrawer()
              },
              optionDisabledInfo: () =>
                checkOperationDisabled({
                  user,
                  aclPermission: [ACLPermissionEnum.READ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                }),
            },
            {
              name: 'Move',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <IoMoveSharp />
                    {'Move to'}
                  </HStackCustom>
                </PaddingBox>
              ),
              icon: IoMoveSharp,
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedMediaCollections.length === 0 &&
                !isFileVersionSelected,
              trigger: () => {
                dispatch([
                  {
                    type: MediaLibraryActions.SET_DRAWER_SEARCH_FILTER,
                    payload: '',
                  },
                  {
                    type: MediaLibraryActions.SET_DRAWER_SEARCH_QUERY,
                    payload: '',
                  },
                ])
                setCopyMoveOperation('move')
                onOpenCopyMoveDrawer()
              },
              optionDisabledInfo: () =>
                checkOperationDisabled({
                  user,
                  aclPermission: [
                    ACLPermissionEnum.READ,
                    ACLPermissionEnum.CONTRIBUTE,
                  ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                }),
            },
            {
              name: 'Rename',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <LiaEdit size={16} />
                    {'Rename'}
                  </HStackCustom>
                </PaddingBox>
              ),
              icon: LiaEdit,
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                !isFileVersionSelected,
              trigger: () => {
                if (selectedFiles.length === 1) {
                  onOpenRenameFileModal()
                } else if (selectedFolders.length === 1) {
                  onOpenRenameFolderModal()
                } else if (selectedMediaCollections.length === 1) {
                  onOpenRenameMediaCollectionModal()
                }
              },
              optionDisabledInfo: () => {
                if (
                  selectedFiles.length +
                    selectedFolders.length +
                    selectedMediaCollections.length !==
                  1
                ) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'select_single_item_to_rename',
                      'Please select a single item to rename.',
                    ),
                  }
                }

                return checkOperationDisabled({
                  user,
                  aclPermission: [
                    ACLPermissionEnum.READ,
                    ACLPermissionEnum.CONTRIBUTE,
                  ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                })
              },
            },
          ],
        },
      },
      {
        name: 'Edit',
        viewConditions:
          !isPublicLink &&
          !isFileVersionSelected &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length > 0 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 0,
        element: () => (
          <PaddingBox>
            <HStackSpacer>
              <HStackCustom>
                <MdOutlineCropOriginal size={16} />
                {'Edit'}
              </HStackCustom>
              <ChevronRightIcon
                fontWeight={400}
                boxSize={5}
                color={colors.imagekitGray['500']}
              />
            </HStackSpacer>
          </PaddingBox>
        ),
        icon: MdOutlineCropOriginal,
        reference: refMap.edit,
        subMenu: {
          items: [
            {
              name: 'ImageKit editor',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <GrImage />
                    {'ImageKit editor'}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedFiles.length > 0 &&
                selectedFolders.length === 0 &&
                selectedMediaCollections.length === 0,
              trigger: onOpenImageEditor,
              optionDisabledInfo: () => {
                if (
                  !selectedFiles
                    .map(file => isEditableImage(file))
                    .every(file => file)
                ) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'image_editor_not_supported_file',
                      'Image editor is not supported for one or more selected files.',
                    ),
                  }
                }

                return checkOperationDisabled({
                  user,
                  aclPermission: [ACLPermissionEnum.READ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                })
              },
            },
            {
              name: 'Legacy browser only editor',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <GrImage />
                    {'Legacy browser only editor'}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedFiles.length > 0 &&
                selectedFolders.length === 0 &&
                selectedMediaCollections.length === 0,
              trigger: onOpenPhotoEditor,
              optionDisabledInfo: () => {
                if (selectedFiles.length > 1) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'image_editor_single_selection',
                      'Legacy image editor is only available for single image selection.',
                    ),
                  }
                }
                if (
                  !selectedFiles
                    .map(file => isEditableImage(file))
                    .every(file => file)
                ) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'image_editor_legacy_not_supported_file',
                      'Legacy image editor is not supported for the selected file.',
                    ),
                  }
                }

                return checkOperationDisabled({
                  user,
                  aclPermission: [ACLPermissionEnum.READ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                })
              },
            },
            {
              name: 'Remove background',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <IoImagesOutline />
                    {'Remove background'}
                  </HStackCustom>
                </PaddingBox>
              ),
              icon: IoImagesOutline,
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedFiles.length > 0 &&
                selectedFolders.length === 0 &&
                selectedMediaCollections.length === 0 &&
                !isFileVersionSelected,
              trigger: onOpenRemoveBgModal,
              optionDisabledInfo: () => {
                if (selectedFiles.length > 1) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'remove_background_single_selection',
                      'Remove background is only available for single image selection.',
                    ),
                  }
                }

                if (
                  !selectedFiles
                    .map(file => allowExtensionOperations(file))
                    .every(file => file)
                ) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'remove_background_not_supported_file',
                      'Remove background is not supported for the selected file.',
                    ),
                  }
                }

                return checkOperationDisabled({
                  user,
                  aclPermission: [ACLPermissionEnum.READ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                })
              },
            },
            {
              name: 'Custom focus area',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <MdCenterFocusWeak />
                    {'Custom focus area'}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedFiles.length > 0 &&
                selectedFolders.length === 0 &&
                selectedMediaCollections.length === 0,
              trigger: onOpenCustomFocusAreaModal,
              optionDisabledInfo: () => {
                if (selectedFiles.length > 1) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'custom_focus_area_single_selection',
                      'Custom focus area is only available for single image selection.',
                    ),
                  }
                }

                if (
                  !selectedFiles
                    .map(file => isEditableImage(file))
                    .every(file => file)
                ) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'custom_focus_area_not_supported_file',
                      'Custom focus area is not supported for the selected file.',
                    ),
                  }
                }

                return checkOperationDisabled({
                  user,
                  aclPermission: [ACLPermissionEnum.READ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                })
              },
            },
          ],
        },
      },
      {
        name: 'Change publication status',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <MdPublishedWithChanges size={16} />
              {t('change_publication_status', 'Change publication status')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: MdPublishedWithChanges,
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length > 0 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected,
        trigger: onOpenPublishFilesModal,
        optionDisabledInfo: () => {
          if (
            !user?.pricingPlan.featuresList.includes(FeaturesEnum.DRAFT_ASSETS)
          ) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'draft_assets_not_available',
                'Draft assets is not available in your current plan. Please upgrade to access this feature.',
              ),
            }
          }

          if (selectedFiles.length > ASSET_IDS_COUNT_LIMIT) {
            return {
              isDisabled: true,
              tooltipMessage: (
                <Trans t={t} i18nKey="publish_selection_limit_exceeded">
                  You cannot publish or unpublish more than{' '}
                  {{ ASSET_IDS_COUNT_LIMIT }} files at once.
                </Trans>
              ),
            }
          }
          return checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'Tags',
        viewConditions:
          !isPublicLink &&
          !isFileVersionSelected &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length > 0 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 0,
        element: () => (
          <PaddingBox>
            <HStackSpacer>
              <HStackCustom>
                <BsTags />
                {'Tags'}
              </HStackCustom>
              <ChevronRightIcon
                fontWeight={400}
                boxSize={5}
                color={colors.imagekitGray['500']}
              />
            </HStackSpacer>
          </PaddingBox>
        ),
        icon: BsTags,
        reference: refMap.tags,
        subMenu: {
          items: [
            {
              name: 'Manage tags',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <BsTags />
                    {'Manage tags'}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedFiles.length === 1 &&
                selectedFolders.length === 0 &&
                selectedMediaCollections.length === 0 &&
                !isFileVersionSelected,
              trigger: () => {
                setTagOperation('updateTags')
                onOpenTagsModal()
              },
              optionDisabledInfo: () =>
                checkOperationDisabled({
                  user,
                  aclPermission: [ACLPermissionEnum.READ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                }),
            },
            {
              name: 'Add auto tags',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <TiTags />
                    {'Add auto tags'}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedFiles.length === 1 &&
                selectedFolders.length === 0 &&
                selectedMediaCollections.length === 0 &&
                !isFileVersionSelected,
              trigger: onOpenAutoTagsModal,
              optionDisabledInfo: () => {
                if (!allowExtensionOperations(selectedFiles[0])) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'auto_tags_not_supported_file',
                      'Auto tags is not supported for the selected file.',
                    ),
                  }
                }
                return checkOperationDisabled({
                  user,
                  aclPermission: [ACLPermissionEnum.READ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                })
              },
            },
            {
              name: 'Add bulk tags',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <CgPlayListAdd />
                    {'Add bulk tags'}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedFiles.length > 1 &&
                selectedFolders.length === 0 &&
                selectedMediaCollections.length === 0 &&
                !isFileVersionSelected,
              trigger: () => {
                setTagOperation('addTagsBulk')
                onOpenTagsModal()
              },
              optionDisabledInfo: () =>
                checkOperationDisabled({
                  user,
                  aclPermission: [ACLPermissionEnum.READ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                }),
            },
            {
              name: 'Remove bulk tags',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <CgPlayListRemove />
                    {'Remove bulk tags'}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedFiles.length > 1 &&
                selectedFolders.length === 0 &&
                selectedMediaCollections.length === 0 &&
                !isFileVersionSelected,
              trigger: () => {
                setTagOperation('removeTagsBulk')
                onOpenTagsModal()
              },
              optionDisabledInfo: () =>
                checkOperationDisabled({
                  user,
                  aclPermission: [ACLPermissionEnum.READ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                }),
            },
          ],
        },
      },
      {
        name: 'Apply path policy',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <MdOutlinePolicy size={16} />
              {t('apply_path_policy', 'Apply path policy')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: MdOutlinePolicy,
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 0 &&
          selectedMediaCollections.length === 0 &&
          selectedFolders.length === 1 &&
          checkPermission({
            user,
            permission: PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE,
          }),
        trigger: onOpenApplyPathPolicyModal,
        optionDisabledInfo: () => {
          if (
            !user?.pricingPlan.featuresList.includes(FeaturesEnum.PATH_POLICY)
          ) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'path_policy_not_available',
                'Path policy is not available in your current plan. Please upgrade to access this feature.',
              ),
            }
          }

          return {
            isDisabled: false,
          }
        },
      },
      {
        name: 'Apply saved extensions',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <IoExtensionPuzzleOutline size={16} />
              {t('apply_saved_extensions', 'Apply saved extensions')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: IoExtensionPuzzleOutline,
        viewConditions:
          !isPublicLink &&
          selectedFiles.length > 0 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }),
        trigger: onOpenApplySavedExtensionModal,
        optionDisabledInfo: () => {
          if (
            !user?.pricingPlan.featuresList.includes(FeaturesEnum.EXTENSIONS)
          ) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'extensions_not_available',
                'Extensions are not available in your current plan. Please upgrade to access this feature.',
              ),
            }
          }

          // Check if all selected files allow extension operations
          const hasIncompatibleFiles = selectedFiles.some(
            file => !allowExtensionOperations(file),
          )
          if (hasIncompatibleFiles) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'extensions_not_supported_for_files',
                'Extensions are not supported for all selected files.',
              ),
            }
          }

          return checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'Restore version',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <MdRestore size={16} />
              {t('restore_version', 'Restore version')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: MdRestore,
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 0 &&
          isFileVersionSelected,
        trigger: onOpenRestoreFileVersionModal,
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
      {
        name: 'divider',
        viewConditions: !isPublicLink && !isFileVersionSelected,
      },
      {
        name: 'Add to media collection',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <HiOutlineFolderAdd size={16} />
              {t('add_to_media_collection', 'Add to media collection')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: HiOutlineFolderAdd,
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          selectedFiles.length +
            selectedFolders.length +
            selectedMediaCollections.length >
            0,
        trigger: onOpenAddToMediaCollection,
        optionDisabledInfo: () => {
          if (
            !user?.pricingPlan.featuresList.includes(
              FeaturesEnum.ACL_AND_COLLECTIONS,
            )
          ) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'media_collections_not_available',
                'Media collection is not available in your current plan. Please upgrade to access this feature.',
              ),
            }
          }

          if (selectedMediaCollections.length > 0) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'cannot_add_collections_to_collections',
                'You cannot add media collections to other media collections.',
              ),
            }
          }
          if (
            selectedFiles.length + selectedFolders.length >
            ASSET_IDS_COUNT_LIMIT
          ) {
            return {
              isDisabled: true,
              tooltipMessage: (
                <Trans
                  t={t}
                  i18nKey="add_to_media_collection_asset_limit_exceeded"
                >
                  You cannot add more than {{ ASSET_IDS_COUNT_LIMIT }} assets at
                  once to media collections.
                </Trans>
              ),
            }
          }
          return checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'Remove from media collections',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <HiOutlineFolderRemove size={16} />
              {t(
                'remove_from_media_collection',
                'Remove from media collections',
              )}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: HiOutlineFolderRemove,
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          selectedFiles.length +
            selectedFolders.length +
            selectedMediaCollections.length >
            0 &&
          currentMediaCollection === undefined,
        trigger: onOpenRemoveAssetFromMultipleMediaCollections,
        optionDisabledInfo: () => {
          if (
            !user?.pricingPlan.featuresList.includes(
              FeaturesEnum.ACL_AND_COLLECTIONS,
            )
          ) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'media_collections_not_available',
                'Media collection is not available in your current plan. Please upgrade to access this feature.',
              ),
            }
          }

          if (
            isRestrictedUser(user?.roles)
              ? (viewableParent as ViewableParentSuccessResponse)
                  .mediaCollection?.permission !== ACLPermissionEnum.MANAGE
              : false
          ) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'cannot_remove_from_collection_no_permission',
                'You do not have permission to remove items from this media collection.',
              ),
            }
          }

          if (selectedMediaCollections.length > 0) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'cannot_remove_collections_from_collections',
                'You cannot remove a collection from other media collections.',
              ),
            }
          }

          if (selectedFiles.length + selectedFolders.length > 1) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'remove_from_collection_single_selection',
                'Only single asset can be removed from media collection at a time.',
              ),
            }
          }
          return checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'Bulk edit custom metadata',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <FiFileMinus />
              {t('bulk_edit_custom_metadata', 'Bulk edit custom metadata')}
            </HStackCustom>
          </PaddingBox>
        ),
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length > 1 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected,
        trigger: onOpenBulkEditCustomMetadataModal,
        optionDisabledInfo: () => {
          if (selectedFiles.length > 1000) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'bulk_edit_custom_metadata_limit_exceeded',
                'Cannot edit custom metadata for more than 1000 files at once.',
              ),
            }
          }

          return checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'Remove from media collection',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <HiOutlineFolderRemove size={16} />
              {t(
                'remove_from_media_collection',
                'Remove from media collections',
              )}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: HiOutlineFolderRemove,
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          (selectedFiles.length > 0 || selectedFolders.length > 0) &&
          selectedMediaCollections.length === 0 &&
          currentMediaCollection !== undefined &&
          folderPath === '/',
        trigger: onOpenRemoveMultipleAssetsFromMediaCollection,
        optionDisabledInfo: () => {
          if (
            !user?.pricingPlan.featuresList.includes(
              FeaturesEnum.ACL_AND_COLLECTIONS,
            )
          ) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'media_collections_not_available',
                'Media collection is not available in your current plan. Please upgrade to access this feature.',
              ),
            }
          }

          if (
            selectedFiles.length + selectedFolders.length >
            ASSET_IDS_COUNT_LIMIT
          ) {
            return {
              isDisabled: true,
              tooltipMessage: (
                <Trans
                  t={t}
                  i18nKey="remove_from_media_collection_asset_limit_exceeded"
                >
                  You cannot remove more than {{ ASSET_IDS_COUNT_LIMIT }} assets
                  at once from media collections.
                </Trans>
              ),
            }
          }
          return checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'divider',
        viewConditions: !isPublicLink && !isFileVersionSelected,
      },
      {
        name: 'Share',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          selectedFiles.length +
            selectedFolders.length +
            selectedMediaCollections.length ===
            1,
        optionDisabledInfo: () => {
          if (
            !user?.pricingPlan.featuresList.includes(
              FeaturesEnum.ACL_AND_COLLECTIONS,
            )
          ) {
            return {
              isDisabled: true,
              tooltipMessage: t(
                'dam_not_available',
                'DAM is not available in your current plan. Please upgrade to access this feature.',
              ),
            }
          }
          return checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
        element: () => (
          <PaddingBox>
            <HStackSpacer>
              <HStackCustom>
                <MdOutlineShare />
                {'Share'}
              </HStackCustom>
              <ChevronRightIcon
                fontWeight={400}
                boxSize={5}
                color={colors.imagekitGray['500']}
              />
            </HStackSpacer>
          </PaddingBox>
        ),
        icon: MdOutlineShare,
        reference: refMap.share,
        subMenu: {
          items: [
            {
              name: 'Invite+',
              icon: MdOutlineShare,
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <FiUserPlus />
                    {'Invite+'}
                  </HStackCustom>
                </PaddingBox>
              ),
              optionDisabledInfo: () => {
                if (
                  !user?.pricingPlan.featuresList.includes(
                    FeaturesEnum.ACL_AND_COLLECTIONS,
                  )
                ) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'dam_not_available',
                      'DAM is not available in your current plan. Please upgrade to access this feature.',
                    ),
                  }
                }
                return checkOperationDisabled({
                  user,
                  aclPermission: [
                    ACLPermissionEnum.READ,
                    ACLPermissionEnum.CONTRIBUTE,
                  ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                })
              },
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                !isFileVersionSelected &&
                selectedFiles.length +
                  selectedFolders.length +
                  selectedMediaCollections.length ===
                  1,
              trigger: () => {
                if (
                  selectedFiles.length === 1 ||
                  selectedFolders.length === 1
                ) {
                  onOpenShareAssetModal()
                } else if (selectedMediaCollections.length === 1) {
                  onOpenShareMediaCollectionModal()
                }
              },
            },
            {
              name: 'Public links',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <MdOutlineAddLink />
                    {'Public links'}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions:
                !isPublicLink &&
                checkPermission({
                  user,
                  permission:
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                }) &&
                selectedFiles.length +
                  selectedFolders.length +
                  selectedMediaCollections.length ===
                  1 &&
                !isFileVersionSelected,
              trigger: onOpenPublicLinksModal,
              optionDisabledInfo: () => {
                if (
                  !user?.pricingPlan.featuresList.includes(
                    FeaturesEnum.PUBLIC_LINKS,
                  )
                ) {
                  return {
                    isDisabled: true,
                    tooltipMessage: t(
                      'public_links_not_available',
                      'Public links is not available in your current plan. Please upgrade to access this feature.',
                    ),
                  }
                }
                return checkOperationDisabled({
                  user,
                  aclPermission: [
                    ACLPermissionEnum.READ,
                    ACLPermissionEnum.CONTRIBUTE,
                  ],
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                })
              },
            },
          ],
        },
      },
      {
        name: 'Embed',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <ImEmbed size={16} />
              {t('embed', 'Embed')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: ImEmbed,
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          selectedFiles[0]?.mime?.includes('video') &&
          selectedFiles[0]?.isPublished,
        trigger: () => {
          handleEmbedModal(true)
        },
      },
      {
        name: 'Copy URL',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <MdContentCopy size={16} />
              {t('copy_url', 'Copy URL')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: MdContentCopy,
        viewConditions:
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 0,
        trigger: () => {
          let finalUrl
          const type = 'text/plain'
          const blobPromise = async () => {
            const data = await getFileURL({
              file: selectedFiles[0],
              ...getFileUrlDynamicArguments,
              request,
            })
            if (data.includes('ik-s=') && data.includes('ik-auth-version')) {
              const url = new URL(data)
              url.searchParams.delete('ik-auth-version')
              url.searchParams.delete('ik-s')
              url.searchParams.delete('ik-t')
              finalUrl = url.toString()
            } else {
              finalUrl = data
            }
            return new Blob([finalUrl], { type })
          }

          if (navigator.clipboard) {
            navigator.clipboard
              .write([
                new ClipboardItem(
                  { [type]: blobPromise() },
                  { presentationStyle: 'unspecified' },
                ),
              ])
              .then(async () => {
                toast({
                  description: t(
                    'copied_url_to_clipboard',
                    'Copied URL to clipboard',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              })
              .catch(err => {
                toast({
                  description: t(
                    'generating_url_to_copy_error',
                    'There was an error in generating the URL',
                  ),
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              })
          }
        },
      },
      {
        name: 'Download',
        element: () => (
          <PaddingBox>
            <HStackSpacer>
              <HStackCustom>
                <HiOutlineDocumentDownload size={16} />
                {t('download', 'Download')}
              </HStackCustom>
              <ChevronRightIcon
                fontWeight={400}
                boxSize={5}
                color={colors.imagekitGray['500']}
              />
            </HStackSpacer>
          </PaddingBox>
        ),
        icon: HiOutlineDocumentDownload,
        viewConditions: !isFileVersionSelected,
        reference: refMap.download,
        subMenu: {
          minWidth: '310px',
          items: [
            {
              name: 'Originals',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    {downloadConfiguration.isOnlyFiles &&
                    downloadConfiguration.totalCount === 1 ? (
                      <HiOutlineDocumentDownload size={16} />
                    ) : (
                      <AiOutlineFileZip size={16} />
                    )}
                    <VStackCustom gap={0}>
                      <HStackCustom gap={4}>
                        <Text fontSize="sm" fontWeight="semibold">
                          {t('download_originals', 'Originals')}
                        </Text>
                        {downloadConfiguration.totalSize && (
                          <Text fontSize="sm">
                            (
                            {numeral(downloadConfiguration.totalSize)
                              .format('0.00 ib')
                              .replace('i', '')}
                            )
                          </Text>
                        )}
                      </HStackCustom>
                      <HStackCustom gap={4}>
                        <Text fontSize="sm">
                          {downloadConfiguration.totalCount} selected asset
                          {downloadConfiguration.totalCount > 1 ? 's' : ''}
                        </Text>
                        {downloadConfiguration.copyText && (
                          <Text fontSize="sm">
                            {downloadConfiguration.copyText}
                          </Text>
                        )}
                      </HStackCustom>
                    </VStackCustom>
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () => void handleDownloads(),
              optionDisabledInfo: () => ({
                isDisabled: false,
              }),
            },
            { name: 'divider', viewConditions: true },
            {
              name: 'LinkedIn Post',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <AiFillLinkedin size={16} />
                      {'LinkedIn Post'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.linkedinPost.width} x{' '}
                        {SocialUrlTransforms.linkedinPost.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(SocialUrlTransforms.linkedinPost),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            {
              name: 'LinkedIn Link Preview',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <AiFillLinkedin size={16} />
                      {'LinkedIn Link Preview'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.linkedLinkPreview.width} x{' '}
                        {SocialUrlTransforms.linkedLinkPreview.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(SocialUrlTransforms.linkedLinkPreview),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            {
              name: 'Facebook Post',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <AiFillFacebook size={16} />
                      {'Facebook Post'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.facebookPost.width} x{' '}
                        {SocialUrlTransforms.facebookPost.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(SocialUrlTransforms.facebookPost),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            {
              name: 'Facebook Story',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <AiFillFacebook size={16} />
                      {'Facebook Story'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.facebookStory.width} x{' '}
                        {SocialUrlTransforms.facebookStory.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(SocialUrlTransforms.facebookStory),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            {
              name: 'X Post',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <BsTwitterX size={14} />
                      {'X (Twitter) Post'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.twitterPost.width} x{' '}
                        {SocialUrlTransforms.twitterPost.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(SocialUrlTransforms.twitterPost),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            {
              name: 'X Link Preview',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <BsTwitterX size={14} />
                      {'X (Twitter) Link Preview'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.twitterLinkPreview.width} x{' '}
                        {SocialUrlTransforms.twitterLinkPreview.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(SocialUrlTransforms.twitterLinkPreview),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            {
              name: 'Instagram Square Post',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <AiFillInstagram size={16} />
                      {'Instagram Square Post'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.instagramSquarePost.width} x{' '}
                        {SocialUrlTransforms.instagramSquarePost.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(SocialUrlTransforms.instagramSquarePost),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            {
              name: 'Instagram Horizontal Post',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <AiFillInstagram size={16} />
                      {'Instagram Horizontal Post'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.instagramHorizontalPost.width} x{' '}
                        {SocialUrlTransforms.instagramHorizontalPost.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(
                  SocialUrlTransforms.instagramHorizontalPost,
                ),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            {
              name: 'Instagram Vertical Post',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <AiFillInstagram size={16} />
                      {'Instagram Vertical Post'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.instagramVerticalPost.width} x{' '}
                        {SocialUrlTransforms.instagramVerticalPost.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(SocialUrlTransforms.instagramVerticalPost),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            {
              name: 'Instagram Story',
              element: ({ isHover }) => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <AiFillInstagram size={16} />
                      {'Instagram Story'}
                    </HStackCustom>
                    {isHover && (
                      <Text fontSize="sm">
                        {SocialUrlTransforms.instagramStory.width} x{' '}
                        {SocialUrlTransforms.instagramStory.height}
                      </Text>
                    )}
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () =>
                void handleDownloads(SocialUrlTransforms.instagramStory),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            { name: 'divider', viewConditions: true },
            {
              name: 'Custom',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <HiOutlineDocumentDownload size={16} />
                      {'Custom'}
                    </HStackCustom>
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () => void onOpenDownloadModal(),
              optionDisabledInfo: () => ({
                isDisabled: downloadConfiguration.isTemplateDisabled,
                tooltipMessage: downloadConfiguration.disabledText,
              }),
            },
            { name: 'divider', viewConditions: true },
            {
              name: 'Download details as CSV',
              element: () => (
                <PaddingBox>
                  <HStackCustom>
                    <HStackCustom>
                      <TbFileSpreadsheet size={16} />
                      {t('download_details_as_csv', 'Download details as CSV')}
                    </HStackCustom>
                  </HStackCustom>
                </PaddingBox>
              ),
              viewConditions: true,
              trigger: () => {
                exportSelectedItemsAsCSV(
                  selectedFiles,
                  selectedFolders,
                  selectedMediaCollections,
                )
              },
              optionDisabledInfo: () => ({
                isDisabled: false,
              }),
            },
          ],
        },
      },
      {
        name: 'divider',
        viewConditions: !isPublicLink && !isFileVersionSelected,
      },
      {
        name: 'Delete',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <RiDeleteBin2Line size={16} />
              {t('delete', 'Delete')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: RiDeleteBin2Line,
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          selectedFiles.length +
            selectedFolders.length +
            selectedMediaCollections.length >
            0,
        trigger: onOpenDeleteMediaModal,
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
      {
        name: 'Delete version',
        element: () => (
          <PaddingBox>
            <HStackCustom>
              <RiDeleteBin2Line size={16} />
              {t('delete_version', 'Delete version')}
            </HStackCustom>
          </PaddingBox>
        ),
        icon: RiDeleteBin2Line,
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 0 &&
          isFileVersionSelected,
        trigger: onOpenDeleteFileVersionModal,
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
    ]
  }, [
    selectedFiles,
    selectedFolders,
    selectedMediaCollections,
    isPublicLink,
    user,
    dashboardServerURL,
    urlPrefix,
    currentMediaCollection,
    folderPath,
    mediaCollectionRoute,
    viewableParent,
    JSON.stringify(background),
    onOpenFile,
    getFileUrlDynamicArguments,
    handleDownloads,
    setCopyMoveOperation,
    setTagOperation,
    onOpenCopyMoveDrawer,
    onOpenRenameFileModal,
    onOpenRenameFolderModal,
    onOpenRenameMediaCollectionModal,
    onOpenPublishFilesModal,
    onOpenTagsModal,
    onOpenAutoTagsModal,
    onOpenRemoveAssetFromMultipleMediaCollections,
    onOpenRemoveMultipleAssetsFromMediaCollection,
    onOpenDeleteMediaModal,
    onOpenDeleteFileVersionModal,
    onOpenRestoreFileVersionModal,
    onOpenRemoveBgModal,
    onOpenCustomFocusAreaModal,
    onOpenShareAssetModal,
    onOpenShareMediaCollectionModal,
    onOpenPublicLinksModal,
    onOpenAddToMediaCollection,
    onOpenApplySavedExtensionModal,
    onOpenApplyPathPolicyModal,
    onOpenImageEditor,
    onOpenDownloadModal,
    handleEmbedModal,
    onOpenPhotoEditor,
  ])

  return contextMenuItems
}
